import React, { useState, useEffect } from 'react';
import { useSurvey } from '../../../context/SurveyContext';
import { CheckCircle, AlertCircle } from 'lucide-react';
import './SurveyResponsePage.css';

const SurveyResponsePage = ({ surveyId }) => {
  const { getSurveyForResponse, addResponse, surveysLoaded } = useSurvey();
  const [survey, setSurvey] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!surveysLoaded) return;
    
    const foundSurvey = getSurveyForResponse(surveyId);
    if (foundSurvey) {
      setSurvey(foundSurvey);
    } else {
      setError('Survey not found or is no longer accepting responses. Please check the link and try again.');
    }
  }, [surveyId, getSurveyForResponse, surveysLoaded]);

  if (!surveysLoaded || (!survey && !error)) {
    return (
      <div className="survey-response-page">
        <div className="survey-loading">
          <div className="loading-spinner"></div>
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-response-page">
        <div className="survey-error">
          <AlertCircle size={48} />
          <h2>Unable to Load Survey</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="survey-response-page">
        <div className="survey-success">
          <CheckCircle size={64} className="success-icon" />
          <h2>Thank You!</h2>
          <p>Your response has been recorded successfully.</p>
          <p className="success-note">You may now close this page.</p>
        </div>
      </div>
    );
  }

  const currentQuestion = survey.questions[currentQuestionIndex];
  const totalQuestions = survey.questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxToggle = (questionId, optionId) => {
    const current = answers[questionId] || [];
    const updated = current.includes(optionId)
      ? current.filter(id => id !== optionId)
      : [...current, optionId];
    setAnswers(prev => ({ ...prev, [questionId]: updated }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(i => i + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(i => i - 1);
    }
  };

  const handleSubmit = () => {
    const response = {
      id: `resp_${Date.now()}`,
      surveyId: survey.id,
      answers,
      submittedAt: new Date().toISOString()
    };
    addResponse(survey.id, response);
    setSubmitted(true);
  };

  const isCurrentAnswered = () => {
    const answer = answers[currentQuestion.id];
    if (!currentQuestion.required) return true;
    if (answer === undefined || answer === null || answer === '') return false;
    if (Array.isArray(answer) && answer.length === 0) return false;
    return true;
  };

  const renderQuestionInput = (question) => {
    switch (question.type) {
      case 'multipleChoice':
        return (
          <div className="response-radio-group">
            {question.options?.map(opt => (
              <label key={opt.id} className="response-radio-option">
                <input
                  type="radio"
                  name={question.id}
                  checked={answers[question.id] === opt.id}
                  onChange={() => handleAnswer(question.id, opt.id)}
                />
                <span className="radio-custom"></span>
                <span className="option-text">{opt.text}</span>
              </label>
            ))}
          </div>
        );

      case 'checkboxes':
        return (
          <div className="response-checkbox-group">
            {question.options?.map(opt => (
              <label key={opt.id} className="response-checkbox-option">
                <input
                  type="checkbox"
                  checked={(answers[question.id] || []).includes(opt.id)}
                  onChange={() => handleCheckboxToggle(question.id, opt.id)}
                />
                <span className="checkbox-custom"></span>
                <span className="option-text">{opt.text}</span>
              </label>
            ))}
          </div>
        );

      case 'dropdown':
        return (
          <select
            className="response-select"
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
          >
            <option value="">Select an option...</option>
            {question.options?.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.text}</option>
            ))}
          </select>
        );

      case 'linearScale':
        const min = question.scaleMin || 1;
        const max = question.scaleMax || 5;
        return (
          <div className="response-scale">
            <span className="scale-label-text">{question.minLabel || min}</span>
            <div className="scale-options">
              {Array.from({ length: max - min + 1 }, (_, i) => min + i).map(num => (
                <label key={num} className="scale-option">
                  <input
                    type="radio"
                    name={question.id}
                    checked={answers[question.id] === num}
                    onChange={() => handleAnswer(question.id, num)}
                  />
                  <span className="scale-number">{num}</span>
                </label>
              ))}
            </div>
            <span className="scale-label-text">{question.maxLabel || max}</span>
          </div>
        );

      case 'likertScale':
        return (
          <div className="response-likert">
            {question.labels?.map((label, index) => (
              <label key={index} className="likert-option">
                <input
                  type="radio"
                  name={question.id}
                  checked={answers[question.id] === index}
                  onChange={() => handleAnswer(question.id, index)}
                />
                <span className="likert-radio"></span>
                <span className="likert-label">{label}</span>
              </label>
            ))}
          </div>
        );

      case 'shortAnswer':
        return (
          <input
            type="text"
            className="response-text-input"
            placeholder="Your answer..."
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            maxLength={question.maxLength}
          />
        );

      case 'longAnswer':
        return (
          <textarea
            className="response-textarea"
            placeholder="Your answer..."
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            maxLength={question.maxLength}
            rows={5}
          />
        );

      case 'yesNo':
        return (
          <div className="response-yesno">
            <label className="yesno-option">
              <input
                type="radio"
                name={question.id}
                checked={answers[question.id] === 'yes'}
                onChange={() => handleAnswer(question.id, 'yes')}
              />
              <span className="yesno-btn">Yes</span>
            </label>
            <label className="yesno-option">
              <input
                type="radio"
                name={question.id}
                checked={answers[question.id] === 'no'}
                onChange={() => handleAnswer(question.id, 'no')}
              />
              <span className="yesno-btn">No</span>
            </label>
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            className="response-text-input"
            placeholder="Enter a number..."
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            min={question.min}
            max={question.max}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            className="response-text-input"
            value={answers[question.id] || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="survey-response-page">
      <div className="survey-response-container">
        <div className="survey-header">
          <h1>{survey.title}</h1>
          {survey.description && <p className="survey-description">{survey.description}</p>}
        </div>

        <div className="survey-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="progress-text">Question {currentQuestionIndex + 1} of {totalQuestions}</span>
        </div>

        <div className="question-card">
          <h2 className="question-text">
            {currentQuestion.text}
            {currentQuestion.required && <span className="required-mark">*</span>}
          </h2>
          {currentQuestion.helpText && (
            <p className="question-help">{currentQuestion.helpText}</p>
          )}
          <div className="question-input">
            {renderQuestionInput(currentQuestion)}
          </div>
        </div>

        <div className="survey-navigation">
          <button
            className="nav-btn prev"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </button>
          
          {currentQuestionIndex < totalQuestions - 1 ? (
            <button
              className="nav-btn next"
              onClick={handleNext}
              disabled={!isCurrentAnswered()}
            >
              Next
            </button>
          ) : (
            <button
              className="nav-btn submit"
              onClick={handleSubmit}
              disabled={!isCurrentAnswered()}
            >
              Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyResponsePage;
